import * as React from "react";
import { Form, Label } from "semantic-ui-react";
import * as moment from "moment";
import { createValidator, validators } from "not-valid";
import { FormBaseComponent, SaveDispatchProps, FormState  } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";
import { BookingTypeName, BookingTypes, ClinicLocationTypesWithoutMobile } from "@teamprevent/common/appointment/models";
import { TaskTypes } from "@teamprevent/common/task/taskActions/models";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";

import { toast } from "@teamprevent/common/toasts";

import { ClinicianSelector } from "../../calendar/components/ClinicianSelector";
import { AdHocCalendarAppointmentModel, BookAppointmentErrors, BookAppointmentTaskListModel } from "../models";
import { AdminUserDetailModel, getUserDisplayName } from "../../crud/adminUser/model";

export interface AdHocCalendarAppointmentFormOwnProps {
    adminUsers: (Partial<AdminUserDetailModel> & { id: string })[];
    bookTask?: BookAppointmentTaskListModel;
    clinicianId?: string;
    startTime?: moment.Moment;
    endTime?: moment.Moment;
    extendTillDay?: moment.Moment;
    minStartTime?: moment.Moment;
    maxEndTime?: moment.Moment;
    initialValue?: AdHocCalendarAppointmentModel;
    bookAppointmentErrors: BookAppointmentErrors | undefined;
    useExtendTillDay?: boolean;
    bookingTypeId?: string;
    hideBookingType?: boolean;
}

type State = FormState<AdHocCalendarAppointmentModel> & { showConflictErrors?: boolean };

export class AdHocCalendarAppointmentForm extends
    FormBaseComponent<AdHocCalendarAppointmentModel, AdHocCalendarAppointmentFormOwnProps, State> {
    private notInPastValidator = createValidator((value: moment.Moment) => value && value.isSameOrAfter(moment()), "Must not be in the past");
    private notesLengthValidator = validators.validLength({ max: 140, message: "Max 140 characters" });

    private bookingTypeOptions: DropdownOption[];

    constructor(props: AdHocCalendarAppointmentFormOwnProps & SaveDispatchProps<AdHocCalendarAppointmentModel>) {
        super(props);
        const clinician = this.getClinicianFromProps();
        this.state = {
            values: props.initialValue || {
                description: "",
                start: props.startTime ?? moment.invalid(),
                end: props.endTime ?? props.startTime?.clone().add(1, "hour") ?? moment.invalid(),
                extendTillDay: props.startTime?.clone().endOf("day"),
                teamPreventUsers: clinician ? [clinician] : []
            },
            valid: props.initialValue ? {} : {
                teamPreventUsers: !!clinician
            }
        };
        this.notInPastValidator = this.notInPastValidator.bind(this);

        this.bookingTypeOptions = BookingTypes.map(t => ({ text: t, value: t })).sort((a, b) => a.text.localeCompare(b.text));
    }

    public render() {
        const { start, end } = this.state.values;
        const calendarHighlight = start && end && { start, end };

        const isNotStartBeforeEndDate = !this.validateStartBeforeEndDate();
        const isNotStartEndDateSameDay = !this.validateStartEndDateSameDay();
        const isNotStartAfterMinStart = !this.validateStartAfterMinStart();
        const isNotEndBeforeMaxEnd = !this.validateEndBeforeMaxEnd();
        const isNotExtendTillDayBeforeEnd = !this.validateExtendTillDay();
        const IsDurationValidForAnnualLeave = !this.validateDurationForAnnualLeave();

        const dateErrors = this.state.showErrors && (<>
            <>{isNotStartBeforeEndDate && <Label basic color="red" pointing content="The start time must be before the end" />}</>
            <>{isNotStartEndDateSameDay && <Label basic color="red" pointing content="The start time must be the same day as the end" />}</>
            <>{isNotStartAfterMinStart && <Label basic color="red" pointing content="The start time must not be before the clinic start time" />}</>
            <>{isNotEndBeforeMaxEnd && <Label basic color="red" pointing content="The end time must not be after the clinic end time" />}</>
            <>{isNotExtendTillDayBeforeEnd && <Label basic color="red" pointing content="The extend till day must not be before the clinic end" />}</>
            <>{IsDurationValidForAnnualLeave &&
                <Label
                    basic
                    color="red"
                    pointing content="If booking type is Annual Leave, the duration between start and end times should be at least 1 hour"
                />}</>
        </>);

        return (
            <Form onSubmit={this.handleSubmit} className="clinic-create-form">
                {(!this.props.hideBookingType && (!this.props.initialValue || !!this.props.initialValue.bookingTypeId)) &&
                    <Input.Dropdown
                        required
                        label="Booking Type"
                        value={this.state.values.bookingTypeId}
                        showErrors={this.state.showErrors}
                        options={this.bookingTypeOptions}
                        onChange={this.onBookingTypeIdChanged}
                    />
                }
                {this.shouldShowAppointmentType() &&
                    <Input.Dropdown
                        required
                        label="Appointment Type"
                        value={this.state.values.locationType}
                        showErrors={this.state.showErrors}
                        options={ClinicLocationTypesWithoutMobile.map(t => ({ text: t, value: t }))}
                        onChange={this.onAppointmentTypeChanged}
                    />
                }
                {(this.props.hideBookingType ||
                    (this.props.initialValue && !this.props.initialValue.bookingTypeId) ||
                    this.showNotesField()) &&
                    <Input.Text
                        label="Notes"
                        value={this.state.values.description}
                        showErrors={this.state.showErrors}
                        onChange={this.onDescriptionChanged}
                        validation={[this.notesLengthValidator]}
                    />
                }
                <Input.Datetime
                    label="Start"
                    value={this.state.values.start}
                    showErrors={this.state.showErrors}
                    validation={[this.notInPastValidator]}
                    required
                    onChange={this.onStartChanged}
                />
                <Input.Datetime
                    label="End"
                    value={this.state.values.end}
                    showErrors={this.state.showErrors}
                    validation={[this.notInPastValidator]}
                    required
                    onChange={this.onEndChanged}
                />
                {this.props.useExtendTillDay &&
                    <Input.Date
                        label="Extend till day"
                        value={this.state.values.extendTillDay}
                        showErrors={this.state.showErrors}
                        onChange={this.onExtendTillDayChanged}
                    />
                }
                {dateErrors}
                <ClinicianSelector
                    selectedClinicians={this.state.values.teamPreventUsers}
                    highlight={calendarHighlight}
                    onCliniciansChanged={this.onCliniciansChanged}
                    conflicts={this.state.showConflictErrors && this.props.bookAppointmentErrors && this.props.bookAppointmentErrors.userClashIds}
                    selectorLimitedToTeamPreventUserIds={this.props.adminUsers?.map(u => u.id)}
                />
                {this.state.showErrors && !this.state.values.teamPreventUsers?.length && <Label basic color="red" pointing>A Clinician is required</Label>}
            </Form>
        );
    }

    public componentDidUpdate(prevProps: AdHocCalendarAppointmentFormOwnProps) {
        if (this.props.startTime && !this.props.startTime.isSame(prevProps.startTime)) {
            this.setTime(this.props.startTime);
        }
        if (this.props.clinicianId !== prevProps.clinicianId) {
            this.updateProperty("teamPreventUsers", [this.getClinicianFromProps()], true);
        }
    }

    public submit(): void {
        if (this.state.values.bookingTypeId === BookingTypeName.other && !this.state.values.description) {
            toast.error("Notes are required");
            return;
        }

        this.setState({ showConflictErrors: true });
        this.handleSubmit({ preventDefault: (): void => undefined } as any);
    }

    private showNotesField(): boolean {
        return this.state.values.bookingTypeId !== undefined;
    }

    private setTime(time: moment.Moment) {
        if (!time) {
            return;
        }
        const endTime = time.clone().add(1, "hour");
        this.updateProperty("start", time, this.notInPastValidator(time).type === "pass");
        this.updateProperty("end", endTime, this.notInPastValidator(endTime).type === "pass");
    }

    private getClinicianFromProps() {
        const clinician = this.props.adminUsers?.find(adminUser => adminUser.id === this.props.clinicianId);
        if (!clinician) {
            return null;
        }
        return { id: clinician.id, name: getUserDisplayName(clinician) ?? "" };
    }

    private shouldShowAppointmentType = () => {
        return this.props.bookTask?.type === TaskTypes.BookHealthSurveillanceConsultationTask;
    };

    private validateStartBeforeEndDate = () => !this.state.values.start || !this.state.values.end || this.state.values.end.diff(this.state.values.start) > 0;
    private validateStartEndDateSameDay = () =>
        !this.state.values.start || !this.state.values.end || this.state.values.end.isSame(this.state.values.start, "day");
    private validateStartAfterMinStart = () => this.props.minStartTime?.isSameOrBefore(this.state.values.start) ?? true;
    private validateEndBeforeMaxEnd = () => this.props.maxEndTime?.isSameOrAfter(this.state.values.end) ?? true;
    private validateExtendTillDay = () => {
        if (!this.props.useExtendTillDay && !this.state.values.extendTillDay) {
            return true;
        }
        const extendTillDay = this.state.values.extendTillDay;
        const isValid = !!(extendTillDay?.endOf("day")?.isSameOrAfter(this.state.values.end));
        return isValid;
    };
    private validateDurationForAnnualLeave = () => {
        const bookingTypeId = this.state.values.bookingTypeId;

        if (!bookingTypeId) {
            return true;
        }

        const startTime = this.state.values.start;
        const endTime = this.state.values.end;

        if (bookingTypeId !== BookingTypeName.annualLeave ||
            !startTime ||
            !endTime) {
            return true;
        }

        const isValid = endTime.diff(startTime, "hours") >= 1 && endTime.diff(startTime, "hours") <= 24;
        return isValid;
    };
    private validateTimes = () =>
        this.validateStartBeforeEndDate() &&
        this.validateStartEndDateSameDay() &&
        this.validateStartAfterMinStart() &&
        this.validateEndBeforeMaxEnd() &&
        this.validateExtendTillDay() &&
        this.validateDurationForAnnualLeave();

    private onAppointmentTypeChanged = (value: string, valid: boolean) => this.updateProperty("locationType", value, valid);
    private onDescriptionChanged = (value: string, valid: boolean) => this.updateProperty("description", value, valid);
    private onBookingTypeIdChanged = (value: string, valid: boolean) => {
        const timesValid = this.validateTimes();
        this.updateProperty("start", this.state.values.start, timesValid);
        this.updateProperty("end", this.state.values.end, timesValid);
        this.updateProperty("extendTillDay", this.state.values.extendTillDay, timesValid);
        this.updateProperty("bookingTypeId", value, valid && timesValid);

        this.setState({ showConflictErrors: false });
    };
    private onStartChanged = (value: moment.Moment, valid: boolean) => {
        const timesValid = this.validateTimes();
        this.updateProperty("start", value && value.utc(true), valid && timesValid);
        this.updateProperty("end", this.state.values.end, this.notInPastValidator(this.state.values.end).type === "pass" && timesValid);
        this.updateProperty("extendTillDay", this.state.values.extendTillDay, timesValid);
        this.setState({ showConflictErrors: false });
    };
    private onEndChanged = (value: moment.Moment, valid: boolean) => {
        const timesValid = this.validateTimes();
        this.updateProperty("end", value && value.utc(true), valid && timesValid);
        this.updateProperty("start", this.state.values.start, this.notInPastValidator(this.state.values.start).type === "pass" && timesValid);
        this.updateProperty("extendTillDay", this.state.values.extendTillDay, timesValid);
        this.setState({ showConflictErrors: false });
    };
    private onExtendTillDayChanged = (value: moment.Moment, valid: boolean) => {
        const timesValid = this.validateTimes();
        this.updateProperty("extendTillDay", value && value.utc(true), valid && timesValid);
        this.updateProperty("start", this.state.values.start, this.notInPastValidator(this.state.values.start).type === "pass" && timesValid);
        this.updateProperty("end", this.state.values.end, this.notInPastValidator(this.state.values.end).type === "pass" && timesValid);
        this.setState({ showConflictErrors: false });
    };
    private onCliniciansChanged = (clinicians: { id: string; name: string }[]) => {
        this.updateProperty("teamPreventUsers", clinicians, !!clinicians.length);
        this.setState({ showConflictErrors: false });
    };
}
